// Live feedback while a template is being transcribed: {{n}} counts per
// component and a preview fill. A deliberate client-side copy of the simplest
// parts of backend/utils/notifications/whatsappTemplate.js, for typing-speed
// feedback only — the server validates and builds authoritatively, and
// nothing here ever rewrites the text the admin pasted.
import type { WhatsAppComponents, WhatsAppValues } from './types';

const PLACEHOLDER = /\{\{\s*(\d+)\s*\}\}/g;

const countVars = (text: string | null | undefined): number =>
  (String(text ?? '').match(PLACEHOLDER) || []).length;

/** Positional fill for the preview. An empty value shows as its {{n}}, so a gap is visible. */
export function fill(text: string, values: string[]): string {
  let i = 0;
  return text.replace(PLACEHOLDER, (m) => {
    const v = values[i++];
    return v ? v : m;
  });
}

interface ComponentCounts {
  header: number;
  body: number;
  buttons: number[];
}

export function countsOf(c: WhatsAppComponents): ComponentCounts {
  return {
    header: c.header ? countVars(c.header.text) : 0,
    body: countVars(c.body?.text),
    buttons: (c.buttons ?? []).map((b) => (b.type === 'url' ? countVars(b.url) : 0)),
  };
}

/** Resizes a values bag to the current counts, keeping what was already typed. */
export function resizeValues(old: WhatsAppValues, counts: ComponentCounts): WhatsAppValues {
  const size = (prev: string[] | undefined, n: number, prefix: string) =>
    Array.from({ length: n }, (_, i) => prev?.[i] ?? `${prefix} ${i + 1}`);
  return {
    header: size(old.header, counts.header, 'Header'),
    body: size(old.body, counts.body, 'Sample'),
    buttons: counts.buttons.map((n, i) => size(old.buttons[i], n, 'suffix')),
  };
}
